/* eslint-disable @typescript-eslint/no-explicit-any */
// Utility functions for case conversion and object manipulation

/**
 * Converts a string to title case (first letter of each word capitalized)
 * @param str - The input string to convert
 * @returns A string with the first letter of each word capitalized
 */
export function toTitleCase(str: string): string {
  // Handle null, undefined, or empty string
  if (!str) return "";

  // Split the string into words, capitalize first letter of each, then join
  return str
    .toLowerCase() // Convert to lowercase first to ensure consistent casing
    .split(/\s+/) // Split on one or more whitespace characters
    .map((word) => {
      // Skip empty strings that might result from multiple spaces
      if (!word) return "";

      // Capitalize first letter, keep rest of the word as is
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Converts camelCase to capitalized words (e.g., "firstName" → "First Name")
 * @param str - The camelCase string to convert
 * @returns A string with words separated by spaces and capitalized
 */
export function camelCaseToCapitalized(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char: string) => char.toUpperCase());
}

/**
 * Converts camelCase to snake_case
 * @param str - The camelCase string to convert
 * @returns A string in snake_case format
 */
export function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Converts snake_case to camelCase
 * @param str - The snake_case string to convert
 * @returns A string in camelCase format
 */
export function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Converts all keys in an object to snake_case
 * @param obj - The object whose keys should be converted
 * @returns A new object with all keys converted to snake_case
 */
export function keysToSnakeCase(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = toSnakeCase(key);

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      result[snakeKey] = keysToSnakeCase(value);
    } else {
      result[snakeKey] = value;
    }
  }

  return result;
}

/**
 * Converts all keys in an object to camelCase
 * @param obj - The object whose keys should be converted
 * @returns A new object with all keys converted to camelCase
 */
export function keysToCamelCase(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const camelKey = toCamelCase(key);

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      result[camelKey] = keysToCamelCase(value);
    } else {
      result[camelKey] = value;
    }
  }

  return result;
}
